import {
  ApiResponse,
  Client,
  ClientUploadData,
  DeleteBatchClientResponse,
  DeleteBatchTransactionResponse,
  ICreateBranchPayload,
  ISupplierBody,
  ITransactionPayload,
  Regional,
  SaveClientPayload,
  SaveRegionalRequest,
  SaveRegionalResponse,
  SaveRegionalsBatchResponse,
} from "../../types";
import api from "../api";

type ListResponseLike = {
  data?: any;
  content?: any;
  items?: any;
};

export const unwrapList = (response: ListResponseLike): any[] => {
  const payload = response?.data ?? response;

  if (Array.isArray(payload)) {
    return payload;
  }

  if (Array.isArray(response?.content)) {
    return response.content;
  }

  if (Array.isArray(response?.items)) {
    return response.items;
  }

  if (payload && typeof payload === "object") {
    if (Array.isArray(payload.data)) {
      return payload.data;
    }
    if (Array.isArray(payload.content)) {
      return payload.content;
    }
    if (Array.isArray(payload.items)) {
      return payload.items;
    }
  }

  return [];
};

export const getBranch = async (): Promise<Regional[]> => {
  const response = await api.get("/regional");

  return unwrapList(response);
};

export const createBranch = async (payload: SaveRegionalRequest): Promise<ApiResponse<SaveRegionalResponse>> => {
  const response = await api.post("/regional", payload);

  return response.data;
};

export const createBranches = async (
  regionals: SaveRegionalRequest[]
): Promise<ApiResponse<SaveRegionalsBatchResponse>> => {
  const response = await api.post("/regional/batch", regionals);

  return response.data;
};

export const editBranch = async ({
  id,
  name,
  active,
}: SaveRegionalRequest): Promise<ApiResponse<SaveRegionalResponse>> => {
  const response = await api.put(`/regional/${id}`, active === undefined ? { name } : { name, active });

  return response.data;
};

export const deleteBranch = async (id: number): Promise<ApiResponse<Record<string, never>>> => {
  const response = await api.delete(`/regional/${id}`);

  return response.data;
};

export const getTransactions = async () => {
  const response = await api.get("/transactions");

  return response.data;
};

export const createTransaction = async (payload: ITransactionPayload, file?: File | null) => {
  const formData = new FormData();

  formData.append("transaction", JSON.stringify(payload));

  if (file) {
    formData.append("file", file);
  }

  const response = await api.post("/transactions", formData);

  return response.data;
};

export const editTransaction = async (id: string, payload: ITransactionPayload, file?: File | null) => {
  if (!file) {
    const response = await api.put(`/transactions/${id}`, payload);
    return response.data;
  }

  const formData = new FormData();

  formData.append("transaction", JSON.stringify(payload));
  formData.append("file", file);

  const response = await api.put(`/transactions/${id}`, formData);

  return response.data;
};

export const getSegments = async () => {
  const response = await api.get("/operations");

  return unwrapList(response);
};

export const createSegments = async (value: string) => {
  const response = await api.post("/operations", { name: value });

  return response.data;
};

export const editSegments = async (payload: ICreateBranchPayload) => {
  const response = await api.post(`/operations`, payload);

  return response.data;
};

export const deleteSegments = async (id: number) => {
  const response = await api.delete(`/operations/${id}`);

  return response.data;
};

export const getCategories = async () => {
  const response = await api.get("/category");

  return unwrapList(response);
};

type CategoryType = { type?: "RECEITA" | "DESPESA" };
type CategoryExpenseType = { expenseType?: "DIRETO" | "OPERACIONAL" };
type CategorySubtractiveType = { isSubtractive?: boolean };

type CreateCategory = ICreateBranchPayload & CategoryType & CategoryExpenseType & CategorySubtractiveType;

export const createCategory = async (payload: CreateCategory) => {
  const response = await api.post("/category", payload);

  return response.data;
};

export const editCategory = async (payload: CreateCategory) => {
  const response = await api.post("/category", payload);

  return response.data;
};

export const deleteCategory = async (id: number) => {
  const response = await api.delete(`/category/${id}`);

  return response.data;
};

export const createSubCategory = async (payload: ICreateBranchPayload) => {
  const response = await api.post("/category", payload);

  return response.data;
};

export const getSubCategories = async (id: string) => {
  const response = await api.get(`/subcategory?categoryId=${id}`);

  return response.data;
};

export const getAllSubCategories = async () => {
  const response = await api.get("/subcategory");

  return response.data;
};

export const createSubCategories = async (id: string, payload: ICreateBranchPayload) => {
  const response = await api.post(`/subcategory`, {
    ...payload,
    displayOrder: 0,
    active: true,
    categoryId: id,
  });

  return response.data;
};

export const editSubCategories = async (id: string, payload: ICreateBranchPayload) => {
  const response = await api.post(`/subcategory`, {
    ...payload,
    displayOrder: 0,
    active: true,
    categoryId: id,
  });

  return response.data;
};

export const deleteSubCategories = async (id: number) => {
  const response = await api.delete(`/subcategory/${id}`);

  return response.data;
};

export const getAccounts = async () => {
  const response = await api.get("/rio/accounts");

  return unwrapList(response);
};

export const getSuppliers = async (type?: string) => {
  const url = type ? `/supplier-client?type=${type}` : "/supplier-client";
  const response = await api.get(url);

  return response.data;
};

export const getContracts = async () => {
  const response = await api.get("/contracts");

  return response.data;
};

export const getAllSuppliers = async () => {
  const response = await api.get("/clients?activeOnly=true");

  return response.data;
};

export const createSuppliers = async (payload: ISupplierBody) => {
  const response = await api.post("/supplier-client ", payload);

  return response.data;
};

export const editSuppliers = async (payload: ISupplierBody) => {
  const response = await api.post(`/supplier-client`, payload);

  return response.data;
};

export const deleteSuppliers = async (id: number) => {
  const response = await api.delete(`/supplier-client/${id}`);

  return response.data;
};

export const createAccounts = async (payload: ICreateBranchPayload) => {
  const response = await api.post("/rio/accounts", payload);

  return response.data;
};

export const getAssets = async () => {
  const response = await api.get("/vehicles");

  return response.data;
};

export const getDrivers = async () => {
  const response = await api.get("/drivers");

  return unwrapList(response);
};

export const getClients = async (): Promise<Client[]> => {
  const response = await api.get("/clients");

  return unwrapList(response);
};

export const createClient = async (payload: SaveClientPayload): Promise<ApiResponse<Client>> => {
  const response = await api.post("/clients", payload);

  return response.data;
};

export const editClient = async ({ id, document, name }: SaveClientPayload): Promise<ApiResponse<Client>> => {
  const response = await api.put(`/clients/${id}`, { document, name });

  return response.data;
};

export const deleteClient = async (id: number): Promise<ApiResponse<Record<string, never>>> => {
  const response = await api.delete(`/clients/${id}`);

  return response.data;
};

export const deleteClientsBatch = async (ids: number[]): Promise<ApiResponse<DeleteBatchClientResponse>> => {
  const response = await api.delete("/clients/batch", { data: { ids } });

  return response.data;
};

export const downloadClientsTemplate = async (): Promise<Blob> => {
  const response = await api.get("/clients/template", {
    responseType: "blob",
  });

  return response.data;
};

export const uploadClientsBatch = async (file: File): Promise<ApiResponse<ClientUploadData>> => {
  const formData = new FormData();
  formData.append("file", file);
  const response = await api.post("/clients/upload", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });

  return response.data;
};

export const getVehicles = async () => {
  const response = await api.get("/vehicles");

  return unwrapList(response);
};

export const getDRESummary = async (startDate: string, endDate: string) => {
  const response = await api.get(`/dre/summary?startDate=${startDate}&endDate=${endDate}`);

  return response.data;
};

type TableDataQuery = {
  page?: number;
  size?: number;
};

interface AssetTreeFilters {
  regionalIds: string[];
  clientIds: string[];
  contracts: string[];
  vehicleIds: string[];
}

export const getTableData = async (
  filters: AssetTreeFilters,
  startDate: string,
  endDate: string,
  type: string,
  query?: TableDataQuery
) => {
  const payload = {
    page: query?.page || 0,
    size: query?.size || 10,
    type,
    startDate,
    endDate,
    ...filters,
  };

  const response = await api.post("/transactions/search", payload);

  return response.data;
};

export const exportTransactions = async (
  startDate: string,
  endDate: string,
  type: "RECEITA" | "DESPESA",
  format: "xlsx" | "csv" = "xlsx"
): Promise<void> => {
  const payload = {
    startDate,
    endDate,
    type,
  };

  const response = await api.post(`/transactions/export/${format}`, payload, {
    responseType: "blob",
  });

  const mimeType = format === "xlsx" ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" : "text/csv";

  const blob = new Blob([response.data], { type: mimeType });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;

  const typeLabel = type === "RECEITA" ? "RECEITAS" : "DESPESAS";
  link.setAttribute("download", `${typeLabel}_${startDate}_${endDate}.${format}`);

  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export const deleteTransaction = async (id: string) => {
  const response = await api.delete(`/transactions/${id}`);
  return response.data;
};

export const deleteTransactionsBatch = async (ids: number[]): Promise<ApiResponse<DeleteBatchTransactionResponse>> => {
  const response = await api.delete("/transactions/batch", { data: { ids } });

  return response.data;
};

export const getDetailTransaction = async (id: string) => {
  const response = await api.get(`/transactions/${id}`);
  return response.data;
};

export const uploadFile = async (id: string, file: File) => {
  const formData = new FormData();
  formData.append("file", file);

  const response = await api.post(`/transactions/upload`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });

  return response.data;
};

export const getFile = async (transactionId: string) => {
  const response = await api.get(`/transactions/${transactionId}/attachments`);
  return response.data;
};

export const deleteFile = async (transactionId: string) => {
  const response = await api.delete(`/transactions/${transactionId}/attachments`);
  return response.data;
};
